import React, { useRef, useMemo, forwardRef } from 'react';
import { styled } from '@compiled/react';
import { mergeRefs } from 'use-callback-ref';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import Button from '@atlaskit/button';
import type { TriggerProps } from '@atlaskit/popup';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { PopupTrigger } from '@atlassian/entry-point-popup';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { ANALYTICS_PACKAGE_NAME } from '../../common/constants.tsx';
import { popupContentEntrypoint } from './popup-content/entrypoint.tsx';
import messages from './messages.tsx';
import { ErrorState } from './error-state/index.tsx';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { DetailsButtonHeaderWrapper } from './styled';

export type Props = {
	filterId: string;
};

const Trigger = forwardRef<
	HTMLElement,
	TriggerProps & {
		triggerRef: React.Ref<HTMLElement>;
	}
>(({ triggerRef, ...props }, ref) => {
	const { formatMessage } = useIntl();
	const mergedRef = mergeRefs([ref, triggerRef]);

	return (
		<Button
			{...props}
			appearance="link"
			isSelected={props['aria-expanded']}
			testId="issue-navigator-custom-filters.ui.details-popup.button"
			ref={mergedRef}
		>
			{formatMessage(messages.filterDetails)}
		</Button>
	);
});

export const DetailsPopup = ({ filterId }: Props) => {
	const isAnonymous = useIsAnonymous();
	const activationId = useActivationId();
	const cloudId = useCloudId();
	const entryPointParams = useMemo(
		() => ({
			filterId,
			activationId,
			cloudId,
			isAnonymous,
		}),
		[filterId, activationId, cloudId, isAnonymous],
	);

	const triggerRef = useRef<HTMLButtonElement | null>(null);
	const titleId = 'issue-navigator-custom-filters.ui.details-popup.popup.title';

	const entryPointProps = useMemo(
		() => ({
			titleId,
			triggerRef,
		}),
		[triggerRef, titleId],
	);

	return (
		<PopupTrigger
			entryPoint={popupContentEntrypoint}
			entryPointParams={entryPointParams}
			entryPointProps={entryPointProps}
			trigger={(props) => <Trigger {...props} triggerRef={triggerRef} />}
			errorFallback={ErrorState}
			onError={(props) => {
				fireErrorAnalytics({
					...props,
					meta: {
						id: 'DetailsPopupEntryPoint',
						packageName: ANALYTICS_PACKAGE_NAME,
						teamName: 'empanada',
					},
					sendToPrivacyUnsafeSplunk: true,
				});
			}}
			shouldRenderToParent
			placement="bottom-start"
			testId="issue-navigator-custom-filters.ui.details-popup.popup"
			role="dialog"
			titleId={titleId}
			fallback={<LoadingFallback />}
		/>
	);
};

const LoadingFallback = () => (
	<LoadingWrapper>
		<Spinner />
	</LoadingWrapper>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const LoadingWrapper = styled.div({
	textAlign: 'center',
	width: '440px',
	paddingTop: token('space.250'),
	paddingRight: token('space.250'),
	paddingBottom: token('space.250'),
	paddingLeft: token('space.250'),
	overflow: 'auto',
	maxHeight: 'calc(100vh - 215px)',
});
